import { useMutation, useQueryClient } from '@tanstack/react-query'
import { DeliverTxResponse } from '@cosmjs/stargate'
import { assertIsDeliverTxSuccess, broadcastTx } from '@/wallet-utils'
import { TxRaw } from 'cosmjs-types/cosmos/tx/v1beta1/tx'
import { queryKeys } from '@/query-keys'
import { fatal } from '@/utils'
import { MutationParameters } from './types'
import { useSelectedChain } from '@/contexts/wallet'

export interface BroadcastRedeemTokenForsharesMutationReturnType {
  transaction: DeliverTxResponse
}

const useBroadcastRedeemTokenForSharesMutation = ({ redeemTokensRaw }: MutationParameters) => {
  const queryClient = useQueryClient()

  const { getSigningStargateClient } = useSelectedChain()

  const handleMutation = async (): Promise<BroadcastRedeemTokenForsharesMutationReturnType> => {
    if (!redeemTokensRaw) {
      throw fatal('Unable to broadcast empty signed transaction.')
    }

    const client = await getSigningStargateClient()

    const bytes = TxRaw.encode(redeemTokensRaw).finish()

    const transaction = await broadcastTx(client, bytes)

    assertIsDeliverTxSuccess(transaction)

    return { transaction }
  }

  const handleSuccess = async () => {
    await queryClient.invalidateQueries({ queryKey: queryKeys.transactionHistoryBase })

    await queryClient.invalidateQueries({ queryKey: queryKeys.chainBalancesBase })

    // Sometimes we have outdated information and the invalidation in `useBroadcastWithdrawTokenizedSharesMutation`
    // doesn't work. So we'll invalidate the query here as well.
    await queryClient.invalidateQueries({ queryKey: queryKeys.transactionHistoryLsmIbcBalancesBase })

    // Make sure that the validators list is updated after the account balances have been updated.
    await queryClient.invalidateQueries({ queryKey: queryKeys.lsmValidatorsBase })
  }

  const handleError = async () => {
    await queryClient.invalidateQueries({ queryKey: queryKeys.transactionHistoryBase })

    // Sometimes we have outdated information and the invalidation in `useBroadcastWithdrawTokenizedSharesMutation`
    // doesn't work. So we'll invalidate the query here as well.
    await queryClient.invalidateQueries({ queryKey: queryKeys.transactionHistoryLsmIbcBalancesBase })

    // Make sure that the validators list is updated after the account balances have been updated.
    await queryClient.invalidateQueries({ queryKey: queryKeys.lsmValidatorsBase })
  }

  return useMutation({
    mutationFn: handleMutation,
    onSuccess: handleSuccess,
    onError: handleError
  })
}

export { useBroadcastRedeemTokenForSharesMutation }
